import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, readableError } from '../../lib/supabase'
import { useSession } from '../../lib/session'
import { usePlatform } from '../../lib/platform'
import { toTitleCase } from '../../lib/format'
import { Shell } from '../../components/Shell'
import PortalFrame from '../../components/PortalFrame'
import { Spinner, initials, avatarTone } from '../../components/ui'

/** A ring with a gap, the value as arc length, the number in the middle. */
function Donut({ value, total, label, color }) {
  const r = 34
  const c = 2 * Math.PI * r
  const pct = total > 0 ? Math.min(value / total, 1) : 0
  return (
    <div className="pf-donut">
      <div className="pf-donut-w">
        <svg width="86" height="86" viewBox="0 0 86 86" aria-hidden="true">
          <circle cx="43" cy="43" r={r} fill="none" stroke="var(--line)" strokeWidth="9" />
          <circle cx="43" cy="43" r={r} fill="none" stroke={color} strokeWidth="9"
                  strokeLinecap="round" strokeDasharray={`${c * pct} ${c}`}
                  transform="rotate(-90 43 43)" />
        </svg>
        <span className="pf-donut-n">{value}</span>
      </div>
      <div className="pf-donut-l">{label}</div>
    </div>
  )
}

const ago = (iso) => {
  if (!iso) return '—'
  const h = Math.floor((Date.now() - new Date(iso)) / 3.6e6)
  if (h < 1) return 'just now'
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

const writers = (s) =>
  s.song_cowriters?.map((c) => c.cowriter_name).filter(Boolean).join(' & ')
  || [s.users?.first_name, s.users?.last_name].filter(Boolean).join(' ')
  || '—'

/** The three columns a submission moves through, in order. Each is a real
 *  songs.status value — not a UI-only grouping — so a song appears in exactly
 *  one column and moving it is a status write, nothing else. */
const COLUMNS = [
  { key: 'ready', label: 'Ready to send', match: (s) => s.status === 'ready_to_send' },
  { key: 'review', label: 'In review', match: (s) => s.status === 'in_review' },
  { key: 'done', label: 'Responded', match: (s) => ['closer_look', 'paired', 'rework'].includes(s.status) },
]

const STATUS_PILL = {
  ready_to_send: ['pf-pill-ready', 'Ready to send'],
  in_review: ['pf-pill-review', 'In review'],
  closer_look: ['pf-pill-ok', 'A closer look'],
  paired: ['pf-pill-ready', 'Paired up'],
  rework: ['pf-pill-warn', 'Rework'],
  home_church_scheduled: ['pf-pill-ready', 'Scheduled'],
  home_church_played: ['pf-pill-ok', 'Played'],
}

/**
 * "Needs you" is the whole inbox on a phone. On a desktop there is room to
 * show the queue either side of it too — what is coming, and what has already
 * been answered — which is the difference between a task list and a picture of
 * how the church is doing.
 */
export default function Inbox() {
  const { church } = useSession()
  const { device } = usePlatform()
  const nav = useNavigate()
  const [songs, setSongs] = useState(null)
  const [err, setErr] = useState(null)

  useEffect(() => {
    if (!church) return
    // RLS decides what comes back — can_review_song() already limits this to
    // songs submitted to a church this person reviews for, so the client
    // filter below is presentation, never the security boundary.
    supabase.from('songs')
      .select('*, song_cowriters(cowriter_name), users!songs_songwriter_id_fkey(first_name, last_name)')
      .eq('submitted_to_church_id', church.id)
      .order('submitted_at', { ascending: true })
      .then(({ data, error }) => {
        if (error) setErr(readableError(error))
        setSongs(data ?? [])
      })
  }, [church])

  if (songs === null) {
    return device === 'phone'
      ? <Shell><Spinner /></Shell>
      : <PortalFrame title="Song review"><Spinner /></PortalFrame>
  }

  /* ---------- phone: unchanged ---------- */
  if (device === 'phone') {
    const waiting = songs.filter((s) => s.status === 'in_review')
    return (
      <Shell>
        <h3 className="sechead">Needs you</h3>
        {waiting.map((s) => (
          <button key={s.id} className="queue" onClick={() => nav(`/review/${s.id}`)}>
            <span className="queue-t">
              {toTitleCase(s.title)}
              <small>{writers(s)} · {ago(s.submitted_at)}</small>
            </span>
            <span className="ar">›</span>
          </button>
        ))}
        <p className="queue-clear">
          {waiting.length
            ? "That's everything for today."
            : 'Nothing waiting on you. That’s everything for today.'}
        </p>
        {err && <p className="err">{err}</p>}
      </Shell>
    )
  }

  /* ---------- desktop / tablet ---------- */
  const decided = songs.filter((s) => ['closer_look', 'paired', 'rework'].includes(s.status))
  const stats = [
    ['A closer look', songs.filter((s) => s.status === 'closer_look').length],
    ['Paired up', songs.filter((s) => s.status === 'paired').length],
    ['Rework', songs.filter((s) => s.status === 'rework').length],
    ['Scheduled', songs.filter((s) => s.status === 'home_church_scheduled').length],
  ]
  const recent = [...songs]
    .sort((a, b) => new Date(b.submitted_at || 0) - new Date(a.submitted_at || 0))
    .slice(0, 6)

  // Everyone who has answered something here — the faces on this queue.
  const onQueue = [...new Map(
    songs.filter((s) => s.decision_by).map((s) => [s.decision_by, s])
  ).values()].slice(0, 4)

  const played = songs.filter((s) => s.status === 'home_church_played').length
  const inReview = songs.filter((s) => s.status === 'in_review').length

  return (
    <PortalFrame title="Song review"
                 sub="Where submissions land — review them, then respond warmly."
                 aside={onQueue.length > 0 && (
                   <div className="pf-queue">
                     <div className="pf-queue-stack">
                       {onQueue.map((s) => (
                         <span key={s.decision_by} className={`pf-song-ava ${avatarTone(s.decision_by)}`}>
                           {initials(writers(s))}
                         </span>
                       ))}
                     </div>
                     <span className="pf-queue-l">On this queue</span>
                   </div>
                 )}>
      <div className="pf-cols pf-cols-queue" style={{ marginBottom: 14 }}>
        {COLUMNS.map((col) => {
          const rows = songs.filter(col.match)
          return (
            <div key={col.key} className="pf-card">
              <div className="pf-cardhead">
                <span className="pf-cardtitle">{col.label}</span>
                <span className="pf-count">{rows.length}</span>
              </div>
              {rows.length === 0 && <p className="pf-empty">Nothing here.</p>}
              {rows.map((s) => {
                const [cls, label] = STATUS_PILL[s.status] ?? ['pf-pill-ready', s.status]
                const scripture = (s.scripture_refs || []).join(', ')
                return (
                  <button key={s.id} className="pf-song" onClick={() => nav(`/review/${s.id}`)}>
                    <span className={`pf-song-ava ${avatarTone(s.songwriter_id || s.id)}`}>
                      {initials(writers(s))}
                    </span>
                    <span className="pf-song-b">
                      <span className="pf-song-t">{toTitleCase(s.title)}</span>
                      <span className="pf-song-m">
                        {writers(s)}
                        {scripture ? ` · ${scripture}` : ''}
                        {col.key === 'review' ? ` · ${ago(s.submitted_at)}` : ''}
                      </span>
                    </span>
                    {col.key === 'done'
                      ? <span className={`pf-pill ${cls}`}>{label}</span>
                      : <span className="pf-song-n">{s.song_number ? `#${s.song_number}` : ''}</span>}
                  </button>
                )
              })}
            </div>
          )
        })}

        <div>
          <div className="pf-accent">This week</div>
          <div className="pf-stats">
            {stats.map(([label, n]) => (
              <div key={label} className="pf-stat">
                <div className="pf-stat-n">{n}</div>
                <div className="pf-stat-l">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="pf-cols pf-cols-2">
        <div className="pf-card">
          <div className="pf-cardhead"><span className="pf-cardtitle">Recent submissions</span></div>
          {recent.length === 0 && <p className="pf-empty">No songs have been sent to your church yet.</p>}
          {recent.length > 0 && (
            <table className="pf-table">
              <thead>
                <tr><th>Song</th><th>Songwriter</th><th>Scripture</th><th>Status</th><th>Song #</th></tr>
              </thead>
              <tbody>
                {recent.map((s) => {
                  const [cls, label] = STATUS_PILL[s.status] ?? ['pf-pill-ready', s.status]
                  return (
                    <tr key={s.id} onClick={() => nav(`/review/${s.id}`)} style={{ cursor: 'pointer' }}>
                      <td>{toTitleCase(s.title)}</td>
                      <td className="pf-td-dim">{writers(s)}</td>
                      <td className="pf-td-dim">{(s.scripture_refs || []).join(', ') || '—'}</td>
                      <td><span className={`pf-pill ${cls}`}>{label}</span></td>
                      <td className="pf-td-dim">{s.song_number ? `#${s.song_number}` : '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="pf-card">
          <div className="pf-cardhead"><span className="pf-cardtitle">This month</span></div>
          <div className="pf-donuts">
            <Donut value={played} total={songs.length} label="Played" color="#4A6FB5" />
            <Donut value={inReview} total={songs.length} label="In review" color="#C4726A" />
          </div>
          <p className="pf-empty" style={{ marginTop: 8, textAlign: 'center' }}>
            {decided.length ? `${decided.length} answered so far.` : 'Nothing answered yet.'}
          </p>
        </div>
      </div>
      {err && <p className="err">{err}</p>}
    </PortalFrame>
  )
}
